/**
 * This module exports various constants and arrays used in the application. These constants
 * are categorized based on their purpose and provide essential data for configuring the
 * application's functionality and user interfaces.
 *
 */

/**
 * Default Mapbox style URL for streets.
 * @type {string}
 * @category Constants
 */
const DEFAULT_STREETS_STYLE_STRING = `mapbox://styles/fzj/ckl258cga13yx18p3bgt6xsc8`;

/**
 * Array of interactive layer IDs for the map.
 * @type {normal: string[],
 *      abnormal: string[] }
 *
 * For the normal strings, "-region" needs to be appended before they'll work
 * while for the abnormal does not need any appending.
 * @category Constants
 */

export const DEFAULT_INTERACTIVE_LAYER_IDS = {
  normal: [
    "excOn",
    "excPV",
    "lcoeOnsh_2050",
    "lcoepv_2050",
    "gwConservative2020",
    "gwConservative2030",
    "gwConservative2050",
    "gwMedium2020",
    "gwMedium2030",
    "gwMedium2050",
    "gwExtreme2020",
    "gwExtreme2030",
    "gwExtreme2050",
  ],
  abnormal: [
    "fact",
    "h2potarea",
    "h2potabs",
    "h2lcoh_2050",
    "h2potgrwaperc",
    "h2potgrwaarea",
    "hydro_2050",
    "hydro_2030",
  ],
};

export const REGION_BASED_INTERACTIVE_LAYER_IDS = {
  africa: {
    normal: ["lcoeOnsh_2030", "lcoepv_2030"],
    abnormal: ["h2lcoh_2030"],
  },
  excludingAfrica: {
    normal: ["lcoeOnsh_2020", "lcoepv_2020"],
    abnormal: ["h2lcoh_2020"],
  },
};

const INTERACTIVE_LAYER_IDS = {
  africa: [
    ...[
      ...DEFAULT_INTERACTIVE_LAYER_IDS.normal,
      ...REGION_BASED_INTERACTIVE_LAYER_IDS.africa.normal,
    ].map((id) => `${id}-region`),
    ...DEFAULT_INTERACTIVE_LAYER_IDS.abnormal,
    ...REGION_BASED_INTERACTIVE_LAYER_IDS.africa.abnormal,
  ],
  excludingAfrica: [
    ...[
      ...DEFAULT_INTERACTIVE_LAYER_IDS.normal,
      ...REGION_BASED_INTERACTIVE_LAYER_IDS.excludingAfrica.normal,
    ].map((id) => `${id}-region`),
    ...DEFAULT_INTERACTIVE_LAYER_IDS.abnormal,
    ...REGION_BASED_INTERACTIVE_LAYER_IDS.excludingAfrica.abnormal,
  ],
};
/**
 * Array of LCOH cost percentages with labels and types.
 * @type {Array<{ label: string, type: string }>}
 * @category Constants
 */
const LCOHCOSTPERCENTAGES = [
  { label: "1%", type: "1%" },
  { label: "25%", type: "25%" },
  { label: "50%", type: "50%" },
  { label: "100%", type: "100%" },
];

export const SOCIO_INDICATOR_TYPE = [
  {
    label: "Qualitative",
    type: "Qualitative",
  },
  {
    label: "Quantitative",
    type: "Quantitative",
  },
];
/**
 * Array of hydro power types with labels and types.
 * @type {Array<{ label: string, type: string }>}
 * @category Constants
 */
const HYDROPOWERTYPE = [
  { label: "conventional", type: "conventional" },
  { label: "Run-of-river plant", type: "ror" },
  { label: "all", type: "all" },
];

/**
 * Array of groundwater status with labels and types.
 * @type {Array<{ label: string, type: string }>}
 * @category Constants
 */
const GROUNDWATERSTATUS = [
  { label: "RCP2.6", type: "opt" },
  { label: "RCP8.5", type: "pess" },
];

/**
 * Array of Mapbox styles with names and URLs.
 * @type {Array<{ name: string, url: string }>}
 * @category Constants
 */
const MAPBOX_STYLES = [
  { name: "streets", url: "ckl258cga13yx18p3bgt6xsc8" },
  { name: "satellite", url: "ckl2648pk150f17msa8pdt5ba" },
];

/**
 * Array of LCOH cost percentages for the factsheet with labels and values.
 * @type {Array<{ label: string, value: string }>}
 * @category Constants
 */
const LCOHCOSTPERCENTAGES_FACTSHEET = [
  { label: "100%", value: "00" },
  { label: "50%", value: "50" },
  { label: "25%", value: "25" },
  { label: "1%", value: "01" },
];

/**
 * Categories for feedback.
 * @type {string[]}
 * @category Constants
 */
const FEEDBACK_CATEGORIES = ["GENERAL", "BUG", "FEATURE REQUEST"];

/**
 * Assignee categories with category names and corresponding IDs.
 * @type {Array<{ category: string, ids: number[] }>}
 * @category Constants
 */
const ASIGNEES_CATEGORIES = [
  { category: "GUI Developers", ids: [1229] },
  { category: "Modellers", ids: [974, 976, 984] },
];

/**
 * Legend labels for groundwater layer group.
 * @type {Object}
 * @category Constants
 */

const GROUNDWATER_LEGEND_LABELS = {
  0: "< 2.5",
  2.5: "2.5 - 10",
  10: "10 - 25",
  25: "25 - 50",
  50: "50 - 100",
  100: "100 - 250",
  250: "> 250",
};

export const SOCIAL_INDICATOR_LEGEND_LABELS = {
  low: "Very low",
  medium: "Medium",
  high: "Very high",
};

export const SOCIAL_INDICATOR_LEGEND_LABELS_ALT = {
  low: "0",
  medium: "0.5",
  high: "1",
};

export const LAND_ELIGIBILITY_LEGEND_LABELS = {
  0: "Ineligible",
  100: "Eligible",
};

const REGIONS = {
  ecowas: ["ecowas"],
  sadc: ["sadc"],
  east: ["east"],
  africa: ["africa"],
  excludingAfrica: ["ecowas", "sadc", "east"],
  all: ["ecowas", "sadc", "east", "africa"],
};
const REGIONS_2 = {
  africa: { name: "africa", path: "/africa" },
  ecowas: { name: "ecowas", path: "/ecowas" },
  sadc: { name: "sadc", path: "/sadc" },
  east: { name: "east", path: "/east" },
};
/**
 * Available pages excluding the home page
 */
const PAGES = [
  {
    name: "Africa",
    path: "/africa",
    modellerPath: "/africa/modeller-interface",
  },
  {
    name: "Southern Africa",
    path: "/sadc",
    modellerPath: "/sadc/modeller-interface",
  },
  {
    name: "Western Africa",
    path: "/ecowas",
    modellerPath: "/ecowas/modeller-interface",
  },
];

export const PROJECTIONS = { EPSG_4326: "EPSG:4326", EPSG_3857: "EPSG:3857" };

export const LANGUAGES = ["en", "de", "fr", "pt"];

export const HYDROGEN_ATTRIBUTES = [
  "Areakm2",
  "CC_1",
  "CC_2",
  "ENGTYPE_1",
  "ENGTYPE_2",
  "GID_0",
  "GID_1",
  "GID_2",
  "GwSPkm3a",
  "H2GTPperc",
  "H2SPAkWhm2",
  "H2SPGWha",
  "H2TPAkWhm2",
  "H2TPGWha",
  "HASC_1",
  "HASC_2",
  "NA00LTEkg2",
  "NA00LTEkg5",
  "NA01LTEkg2",
  "NA01LTEkg3",
  "NA01LTEkg4",
  "NA01LTEkg5",
  "NA25LTEkg2",
  "NA25LTEkg3",
  "NA25LTEkg4",
  "NA25LTEkg5",
  "NA50LTEkg2",
  "NA50LTEkg3",
  "NA50LTEkg4",
  "NA50LTEkg5",
  "NAGTPperc",
  "NAME_0",
  "NAME_1",
  "NAME_2",
  "NASPTWha",
  "NATPTWha",
  "NL_NAME_1",
  "NL_NAME_2",
  "ProjReg",
  "TYPE_1",
  "TYPE_2",
  "VARNAME_1",
  "VARNAME_2",
  "indices",
  "region_id",
  "00LhEkg30",
  "00LhEkg40",
  "00LhEkg50",
  "01LhEkg30",
  "01LhEkg40",
  "01LhEkg50",
  "02LhEkg30",
  "02LhEkg40",
  "02LhEkg50",
  "04LhEkg30",
  "04LhEkg40",
  "04LhEkg50",
  "06LhEkg30",
  "06LhEkg40",
  "06LhEkg50",
  "10LhEkg30",
  "10LhEkg40",
  "10LhEkg50",
  "15LhEkg30",
  "15LhEkg40",
  "15LhEkg50",
  "20LhEkg30",
  "20LhEkg40",
  "20LhEkg50",
  "25LhEkg30",
  "25LhEkg40",
  "25LhEkg50",
  "30LhEkg30",
  "30LhEkg40",
  "30LhEkg50",
  "50LhEkg30",
  "50LhEkg40",
  "50LhEkg50",
  "70LhEkg30",
  "70LhEkg40",
  "70LhEkg50",
  "75LhEkg30",
  "75LhEkg40",
  "75LhEkg50",
  "80LhEkg30",
  "80LhEkg40",
  "80LhEkg50",
  "85LhEkg30",
  "85LhEkg40",
  "85LhEkg50",
  "90LhEkg30",
  "90LhEkg40",
  "90LhEkg50",
  "96LhEkg30",
  "96LhEkg40",
  "96LhEkg50",
  "98LhEkg30",
  "98LhEkg40",
  "98LhEkg50",
  "99LhEkg30",
  "99LhEkg40",
  "99LhEkg50",
];

export {
  DEFAULT_STREETS_STYLE_STRING,
  INTERACTIVE_LAYER_IDS,
  LCOHCOSTPERCENTAGES,
  HYDROPOWERTYPE,
  GROUNDWATERSTATUS,
  MAPBOX_STYLES,
  LCOHCOSTPERCENTAGES_FACTSHEET,
  FEEDBACK_CATEGORIES,
  ASIGNEES_CATEGORIES,
  GROUNDWATER_LEGEND_LABELS,
  REGIONS,
  PAGES,
  REGIONS_2,
};
